import React, { useState, useRef, useEffect } from 'react';
import ChatOverlay from './ChatOverlay';
import '../styles/ChatBotIcon.css';

const tooltips = [
  "Make a Talent Search !",
  "Need to schedule an interview?",
  "Check dashboard analytics!",
  "Let's manage employee records!"
];

const ChatBotIcon: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipText, setTooltipText] = useState(tooltips[0]);
  const [showTooltip, setShowTooltip] = useState(true);

  // Initial position: right side, bottom of screen
  const [position, setPosition] = useState({
    x: typeof window !== 'undefined' ? window.innerWidth - 130 : 300,
    y: typeof window !== 'undefined' ? window.innerHeight - 100 : 300
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });
  const hasMoved = useRef(false);

  // Handle window resize to keep it on screen
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        const maxX = window.innerWidth - 60;
        const maxY = window.innerHeight - 60;
        return {
          x: Math.max(0, Math.min(prev.x, maxX)),
          y: Math.max(0, Math.min(prev.y, maxY))
        };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      setShowTooltip(false); // trigger fade out
      setTimeout(() => {
        index = (index + 1) % tooltips.length;
        setTooltipText(tooltips[index]);
        setShowTooltip(true); // trigger fade in
      }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y
    };
    setIsDragging(true);
    hasMoved.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      hasMoved.current = true;
    }

    const maxX = window.innerWidth - 60;
    const maxY = window.innerHeight - 60;

    const newX = Math.max(0, Math.min(dragRef.current.initialX + dx, maxX));
    const newY = Math.max(0, Math.min(dragRef.current.initialY + dy, maxY));

    setPosition({ x: newX, y: newY });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsDragging(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setIsOpen(true);
  };

  return (
    <>
      {!isOpen && (
        <div
          className={`chatbot-fab ${isDragging ? 'dragging' : ''}`}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            right: 'auto',
            bottom: 'auto',
            position: 'fixed'
          }}
        >
          <div className={`chatbot-fab-tooltip ${showTooltip && !isDragging ? 'visible' : 'hidden'}`}>
            {tooltipText}
          </div>
          <div className="chatbot-fab-glow"></div>
          <div className="chatbot-fab-icon">
            <img src="/assistant.png" alt="Assistant" style={{ width: '75%', height: '75%', objectFit: 'contain' }} draggable="false" />
          </div>
        </div>
      )}
      {isOpen && <ChatOverlay onClose={() => setIsOpen(false)} />}
    </>
  );
};

export default ChatBotIcon;
